import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronDown, 
  ChevronUp,
  HelpCircle,
  Lightbulb,
  Zap,
  Shield,
  DollarSign
} from 'lucide-react';

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqCategories = [
    {
      title: "Safety Net Installation",
      icon: <Shield className="h-6 w-6" />,
      color: "from-blue-400 to-blue-600",
      questions: [
        {
          question: "How long does safety net installation take?",
          answer: "Residential safety net installations typically take 2-4 hours, commercial projects 1-2 days, and industrial installations 2-5 days depending on the size and complexity of the area to be covered."
        },
        {
          question: "What is the warranty on safety nets?",
          answer: "Our safety nets come with a 5-year warranty on materials and workmanship. We provide comprehensive warranty coverage for all our safety net installations."
        },
        {
          question: "Do safety nets require maintenance?",
          answer: "Safety nets are low-maintenance but we recommend regular inspection every 6 months and cleaning as needed. We provide maintenance services to ensure optimal performance."
        }
      ]
    },
    {
      title: "Cost & Investment",
      icon: <DollarSign className="h-6 w-6" />,
      color: "from-green-400 to-green-600",
      questions: [
        {
          question: "How much do safety nets cost?",
          answer: "Safety net costs vary based on size, material, and installation complexity. We provide competitive pricing with free quotes for all projects. Contact us for a detailed estimate."
        },
        {
          question: "What factors affect safety net pricing?",
          answer: "Pricing depends on area size, net material quality, installation height, complexity, and project duration. We offer transparent pricing with no hidden costs."
        },
        {
          question: "Do you provide free quotes?",
          answer: "Yes, we provide completely free quotes and site surveys for all safety net projects. Our experts will assess your requirements and provide detailed pricing."
        }
      ]
    },
    {
      title: "Technical Questions",
      icon: <Zap className="h-6 w-6" />,
      color: "from-purple-400 to-purple-600",
      questions: [
        {
          question: "What materials are used for safety nets?",
          answer: "We use high-quality HDPE (High-Density Polyethylene) and nylon materials that are UV-resistant, weatherproof, and meet international safety standards."
        },
        {
          question: "How strong are the safety nets?",
          answer: "Our safety nets can withstand significant loads and are designed to catch falling objects or people. They meet or exceed industry safety standards for construction and industrial applications."
        },
        {
          question: "Can safety nets be customized?",
          answer: "Yes, we provide fully customized safety nets in various sizes, colors, and specifications to meet your specific requirements and safety standards."
        }
      ]
    },
    {
      title: "Maintenance & Support",
      icon: <Shield className="h-6 w-6" />,
      color: "from-yellow-400 to-orange-500",
      questions: [
        {
          question: "How often should safety nets be inspected?",
          answer: "We recommend professional inspection every 6 months and after any severe weather. Regular visual checks should be done monthly to ensure nets are secure and undamaged."
        },
        {
          question: "What if a safety net gets damaged?",
          answer: "We provide 24/7 emergency support and repair services. Damaged nets should be replaced immediately for safety. We offer quick repair and replacement services."
        },
        {
          question: "Do you provide maintenance services?",
          answer: "Yes, we offer comprehensive maintenance packages including regular inspections, cleaning, repairs, and replacements to ensure your safety nets remain effective and compliant."
        }
      ]
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Sports Nets Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover opacity-50"
            onError={(e) => {
              console.log('Video failed to load, using fallback image');
              const fallbackDiv = document.createElement('div');
              fallbackDiv.className = 'absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat';
              fallbackDiv.style.backgroundImage = `url('https://img.freepik.com/premium-photo/blue-solar-photo-voltaic-panels-system-producing-renewable-clean-energy-rural-landscape-setting-sun_127089-3464.jpg?semt=ais_hybrid&w=740&q=80')`;
              if (e.currentTarget.parentNode) {
                e.currentTarget.parentNode.appendChild(fallbackDiv);
                e.currentTarget.style.display = 'none';
              }
            }}
            onLoadStart={() => {
              console.log('Attempting to load sports-nets video...');
            }}
            onCanPlay={() => {
              console.log('Video loaded successfully and can play');
            }}
            onLoadedData={(e) => {
              console.log('Video data loaded');
              e.currentTarget.play().catch(err => console.log('Auto-play failed:', err));
            }}
          >
            <source src="/videos/sports-nets.mp4" type="video/mp4" />
            <source src="/videos/sports-nets.webm" type="video/webm" />
          </video>
        </div>
        
        {/* Dark Overlay for Better Text Visibility */}
        <div className="absolute inset-0 bg-black opacity-25"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <HelpCircle className="h-20 w-20 text-yellow-400 mx-auto mb-6" />
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-white max-w-3xl mx-auto font-medium">
              Get answers to common questions about safety nets, installation, costs, and maintenance. 
              Everything you need to know about safety net solutions.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {faqCategories.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                {/* Category Header */}
                <div className={`bg-gradient-to-r ${category.color} text-white p-6`}>
                  <div className="flex items-center space-x-3">
                    {category.icon}
                    <h2 className="text-2xl font-bold">{category.title}</h2>
                  </div>
                </div>
                
                {/* Questions */}
                <div className="p-6">
                  {category.questions.map((item, questionIndex) => {
                    const itemIndex = categoryIndex * 3 + questionIndex;
                    const isOpen = openItems.includes(itemIndex);
                    
                    return (
                      <div key={questionIndex} className="mb-4 last:mb-0">
                        <button
                          onClick={() => toggleItem(itemIndex)}
                          className="w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-between"
                        >
                          <span className="font-medium text-gray-900 pr-4">
                            {item.question}
                          </span>
                          {isOpen ? (
                            <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-2 p-4 bg-blue-50 rounded-lg"
                          >
                            <p className="text-gray-700 leading-relaxed">
                              {item.answer}
                            </p>
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Help Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Lightbulb className="h-16 w-16 text-yellow-500 mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our safety net experts are here to help.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+918074616049"
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center"
              >
                <Zap className="mr-2 h-5 w-5" />
                Call Our Experts
              </a>
              <a
                href="https://wa.me/918074616049"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-yellow-500 hover:bg-yellow-500 hover:text-gray-900 text-yellow-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
